// ============================================
// restaurant/auth/RestaurantSignUp.js
// Dedicated Restaurant Sign Up Flow
// ============================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Lock, Utensils, Store, Loader2, ArrowLeft, ChevronRight, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../common/context/AuthContext';
import Toast from '../../common/components/Toast';
import { RESTAURANT_API_URL } from '../../api/config';
import RestaurantBg from '../../Pictures/restaurant.jpg';

function InputField({ label, icon: Icon, ...props }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && <label className="text-sm font-semibold text-gray-900 dark:text-gray-300">{label}</label>}
      <div className="relative">
        <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600" />
        <input
          className="w-full h-12 rounded-xl px-4 pl-12 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all text-sm"
          {...props}
        />
      </div>
    </div>
  );
}

function PrimaryBtn({ loading, loadLabel, children, ...props }) {
  return (
    <button
      className="w-full h-12 rounded-2xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 active:scale-95 transition-all shadow-lg shadow-blue-600/20 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      disabled={loading}
      {...props}
    >
      {loading ? <><Loader2 className="animate-spin" size={18} />{loadLabel || 'Please wait...'}</> : children}
    </button>
  );
}

const STEP = {
  LANGUAGE: 'language',
  EMAIL: 'email',
  DETAILS: 'details',
  OTP: 'otp',
};

export default function RestaurantSignUp() {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const [step, setStep]                     = useState(STEP.LANGUAGE);
  const [email, setEmail]                   = useState('');
  const [emailError, setEmailError]         = useState('');
  const [password, setPassword]             = useState('');
  const [loading, setLoading]               = useState(false);
  const [restaurantId, setRestaurantId]     = useState('');
  const [restaurantName, setRestaurantName] = useState('');
  const [outletName, setOutletName]         = useState('');
  const [otp, setOtp]                       = useState('');
  const [language, setLanguage]             = useState('en');

  const [toast, setToast]                   = useState({ show: false, message: '', type: 'success' });
  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  const closeToast = () => setToast(t => ({ ...t, show: false }));

  const validateEmail = (text) => {
    setEmail(text);
    if (emailError) setEmailError('');
    if (!text.length) return;
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(text))
      setEmailError('Please enter a valid email address');
  };

  const handleEmailNext = () => {
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setStep(STEP.DETAILS);
  };

  const handleRestaurantSignUp = async () => {
    if (!restaurantName || !outletName || !password) {
      showToast('Please fill in all fields', 'error'); return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${RESTAURANT_API_URL}/auth/signup`, {
        email, password, restaurantName, outletName, language
      });
      if (res.data.success) {
        setRestaurantId(res.data.restaurantId);
        localStorage.setItem('businessType', 'restaurant');
        showToast('Check your email for the OTP verification code');
        setStep(STEP.OTP);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Registration failed', 'error');
    } finally { setLoading(false); }
  };

  const handleVerifyOTP = async () => {
    if (!otp) { showToast('Please enter the OTP', 'error'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${RESTAURANT_API_URL}/auth/verify-otp`, {
        restaurantId, otp,
      });
      if (res.data.success) {
        showToast('Restaurant account created and verified successfully!');
        setTimeout(() => navigate('/restaurant/signin'), 1500);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'OTP verification failed', 'error');
    } finally { setLoading(false); }
  };

  return (
    <div
      className="h-screen overflow-hidden flex flex-col"
      style={{
        backgroundImage: `url('${RestaurantBg}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black/55 dark:bg-black/70 pointer-events-none z-0" />
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

      {/* Header */}
      <header className="relative z-10 flex-shrink-0 flex items-center justify-between border-b border-white/10 px-6 py-4 bg-white/10 dark:bg-black/20 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 text-blue-400 flex items-center justify-center">
            <Utensils size={22} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white leading-none">TOBO RESTAURANT</h2>
            <p className="text-xs text-white/70 leading-none">BILL IT, DONE!</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-white/70 hidden sm:block">Need Salon Account?</span>
          <button
            onClick={() => navigate('/salon/signup')}
            className="px-4 h-9 bg-pink-600 text-white text-sm font-bold rounded-xl hover:bg-pink-700 transition-all"
          >
            Salon Sign Up
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-4 overflow-hidden">
        <div className="w-full max-w-[460px] max-h-full overflow-y-auto bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl border border-white/30 dark:border-gray-700/50 shadow-2xl">

          <div className="flex flex-wrap justify-between items-start gap-3 px-8 pt-8 pb-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-3">
              {step !== STEP.LANGUAGE && (
                <button
                  onClick={() => {
                    if (step === STEP.OTP) setStep(STEP.DETAILS);
                    else if (step === STEP.DETAILS) setStep(STEP.EMAIL);
                    else if (step === STEP.EMAIL) setStep(STEP.LANGUAGE);
                  }}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500"
                >
                  <ArrowLeft size={18} />
                </button>
              )}
              <div>
                <h1 className="text-2xl font-black text-gray-900 dark:text-white">
                  {step === STEP.LANGUAGE && 'Select Language'}
                  {step === STEP.EMAIL && 'Create Restaurant Account'}
                  {step === STEP.DETAILS && 'Restaurant Details'}
                  {step === STEP.OTP && 'Verify Email'}
                </h1>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {step === STEP.LANGUAGE && 'Choose your preferred display language'}
                  {step === STEP.EMAIL && 'Start managing your restaurant with TOBO'}
                  {step === STEP.DETAILS && `Setting up account for ${email}`}
                  {step === STEP.OTP && 'Enter the OTP code sent to your email'}
                </p>
              </div>
            </div>
          </div>

          <div className="px-8 py-6 flex flex-col gap-4">

            {/* STEP: LANGUAGE */}
            {step === STEP.LANGUAGE && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-300">
                    Select Your Language
                  </label>
                  <p className="text-xs text-gray-500">
                    This language will automatically set the default for your account. You can change this anytime in Settings.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mt-2 max-h-[220px] overflow-y-auto pr-1">
                    {[
                      { code: 'en', name: 'English' },
                      { code: 'hi', name: 'Hindi (हिंदी)' },
                      { code: 'pa', name: 'Punjabi (ਪੰਜਾਬੀ)' },
                      { code: 'bn', name: 'Bengali (বাংলা)' },
                      { code: 'mr', name: 'Marathi (मराठी)' },
                      { code: 'gu', name: 'Gujarati (ગુજરાતી)' },
                      { code: 'ta', name: 'Tamil (தமிழ்)' },
                      { code: 'te', name: 'Telugu (తెలుగు)' },
                      { code: 'kn', name: 'Kannada (ಕನ್ನಡ)' },
                      { code: 'ml', name: 'Malayalam (മലയാളം)' },
                      { code: 'or', name: 'Odia (ଓଡ଼ିଆ)' },
                      { code: 'as', name: 'Assamese (অসমীয়া)' },
                      { code: 'ur', name: 'Urdu (اردو)' },
                    ].map(lang => (
                      <button
                        key={lang.code}
                        type="button"
                        onClick={() => setLanguage(lang.code)}
                        className={`px-4 py-3 rounded-xl border text-sm font-medium transition-all text-left flex items-center justify-between ${
                          language === lang.code
                            ? 'border-blue-600 bg-blue-50/50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                            : 'border-gray-200 hover:bg-gray-50 text-gray-700 dark:border-gray-700 dark:hover:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        <span>{lang.name}</span>
                        {language === lang.code && <CheckCircle2 size={16} className="text-blue-600" />}
                      </button>
                    ))}
                  </div>
                </div>
                <PrimaryBtn onClick={() => setStep(STEP.EMAIL)}>
                  Continue <ChevronRight size={16} />
                </PrimaryBtn>
              </div>
            )}

            {/* STEP 1: EMAIL */}
            {step === STEP.EMAIL && (
              <div className="flex flex-col gap-4">
                <InputField
                  label="Email Address"
                  icon={Mail}
                  type="email"
                  placeholder="Enter your restaurant email address"
                  value={email}
                  onChange={e => validateEmail(e.target.value)}
                  autoCapitalize="none"
                />
                {emailError && <p className="text-xs text-red-600 dark:text-red-400 -mt-2">{emailError}</p>}
                <PrimaryBtn onClick={handleEmailNext} disabled={!email || !!emailError}>
                  Continue <ChevronRight size={16} />
                </PrimaryBtn>
              </div>
            )}

            {/* STEP 2: DETAILS */}
            {step === STEP.DETAILS && (
              <div className="flex flex-col gap-4">
                <InputField
                  label="Restaurant Name"
                  icon={Utensils}
                  type="text"
                  placeholder="e.g. Royal Wazwan Restaurant"
                  value={restaurantName}
                  onChange={e => setRestaurantName(e.target.value)}
                />
                <InputField
                  label="Primary Outlet Name"
                  icon={Store}
                  type="text"
                  placeholder="e.g. Main Branch"
                  value={outletName}
                  onChange={e => setOutletName(e.target.value)}
                />
                <InputField
                  label="Create Password"
                  icon={Lock}
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <PrimaryBtn loading={loading} loadLabel="Creating Account..." onClick={handleRestaurantSignUp}>
                  Create Restaurant <ChevronRight size={16} />
                </PrimaryBtn>
              </div>
            )}

            {/* STEP 3: OTP */}
            {step === STEP.OTP && (
              <div className="flex flex-col gap-4">
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                  Enter the 6-digit OTP code sent to <strong className="text-gray-900 dark:text-white">{email}</strong>
                </p>
                <div className="flex justify-center my-2">
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="123456"
                    value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                    className="w-48 h-12 text-center text-2xl font-black tracking-widest rounded-xl border-2 border-blue-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
                  />
                </div>
                <PrimaryBtn loading={loading} loadLabel="Verifying..." onClick={handleVerifyOTP}>
                  Verify &amp; Activate Account <CheckCircle2 size={16} />
                </PrimaryBtn>
              </div>
            )}

            <div className="pt-4 border-t border-gray-100 dark:border-gray-800 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Already have a restaurant account?{' '}
                <button onClick={() => { signOut(); navigate('/restaurant/signin'); }} className="font-bold text-blue-600 hover:underline">
                  Sign In
                </button>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
